<template>
  <PrmMultiselect
    ref="multiselect"
    v-model="localDraftValue"
    :options="options"
    :virtual-scroller-options="virtualScrollerOptions"
    :option-disabled="optionDisabled"
    @filter="emit('filter', $event)"
    @hide="onHide"
  >
    <template #footer>
      <div
        class="flex-row flex-row_spaced"
        style="
          margin: var(--gap);
          align-items: center;
        "
      >
        <QmulusButton
          severity="tertiary"
          :label="t('actions.clear')"
          @click="setDraftValue([])"
        />
        <span>
          {{ t('common.selected') }}: {{ localDraftValue.length }}
        </span>
        <SemanticButtonGroup
          :buttons="BUTTONS"
        />
      </div>
    </template>
    <template
      v-if="!!$slots.value && hasSlotContent($slots.value)"
      #value="valueSlotProps"
    >
      <slot
        name="value"
        v-bind="valueSlotProps"
      />
    </template>
    <template #option="optionSlotProps">
      <slot
        name="option"
        v-bind="optionSlotProps"
      />
    </template>
  </PrmMultiselect>
</template>

<script setup lang="ts" generic="VALUES, OPTION">
import PrmMultiselect, {
  MultiSelectProps,
} from '@NitrexGroup/primevue/multiselect';
import {
  shallowRef,
  useTemplateRef,
  watch,
} from 'vue';

import useLibraryI18n from '../../i18n/composable.ts';
import {
  hasSlotContent,
} from '../../utils/vue';
import QmulusButton from '../button/qmulus-button.vue';
import SemanticButtonGroup from '../button/semantic-button-group.vue';

const props = defineProps<{
  modelValue: VALUES[],
  options: OPTION[],
  showResetButton?: boolean,
  virtualScrollerOptions?: MultiSelectProps['virtualScrollerOptions'],
  optionDisabled?: MultiSelectProps['optionDisabled'],
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: VALUES[]): void,
  (e: 'update:draftValue', value: VALUES[]): void,
  (e: 'filter', value: {
    value?: string,
  }): void,
  (e: 'hide'): void,
}>();

defineSlots<{
  option?(props: {
    option: OPTION,
  }): any,
  value?(props: {
    value: VALUES[],
    placeholder?: string,
  }): any,
}>();

const {
  t,
} = useLibraryI18n();

let skipResetAfterHide = false;

const multiselect = useTemplateRef('multiselect');
const localDraftValue = shallowRef<VALUES[]>([]);

function setDraftValue(value: VALUES[]) {
  localDraftValue.value = value;
  emit('update:draftValue', value);
}

const BUTTONS = {
  accept: {
    onClick: () => {
      emit('update:modelValue', localDraftValue.value);
      skipResetAfterHide = true;
      multiselect.value?.hide();
    },
  },
  ...(
    props.showResetButton && {
      reset: {
        onClick: () => {
          setDraftValue([
            ...props.modelValue,
          ]);
        },
      },
    }
  ),
};

function onHide() {
  // Reset all changes on hide, except when Accept is clicked
  if (!skipResetAfterHide) {
    setDraftValue([
      ...props.modelValue,
    ]);
  }
  skipResetAfterHide = false;
  emit('hide');
}

watch(
  () => props.modelValue,
  (value) => {
    localDraftValue.value = [
      ...value,
    ];
  }, {
    immediate: true,
    deep: true,
  },
);

watch(
  localDraftValue,
  (value) => {
    const nextValue = [
      ...value,
    ];

    emit('update:draftValue', nextValue);
    if (
      // Hack to update modelValue if overlay is closed and option was deleted by clicking cross icon in the multiselect input
      // If overlay is open, modelValue will be updated when Accept button is clicked
      (multiselect.value as any)?.overlayVisible !== true
      && nextValue.length !== props.modelValue.length
    ) {
      emit('update:modelValue', nextValue);
    }
  },
  {
    deep: true,
  },
);

</script>
